import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invokeErrorMessage } from "@/lib/invokeErrorMessage";
import type { AdminUserDetails } from "@produce/shared";

/**
 * The admin panel's support view of one user.
 *
 * Goes through the `admin-user-details` edge function rather than the tables:
 * crew settings, push registrations and other producers' briefings are not
 * readable by an admin over RLS, and the function is the one audited place
 * where the service role is used for this read.
 */
export function useAdminUserDetails(userId: string | undefined) {
  return useQuery<AdminUserDetails>({
    queryKey: ["admin-user-details", userId],
    enabled: Boolean(userId),
    // Support work is bursty: a few refreshes while looking at one account,
    // then nothing. A short stale window avoids re-running a dozen queries on
    // every tab focus without ever showing badly out-of-date state.
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("admin-user-details", {
        body: { targetUserId: userId },
      });

      // supabase-js discards the response body on a non-2xx and reports its
      // own "Edge Function returned a non-2xx status code". This page exists
      // to stop an admin going to the database, so its error card must say
      // which of NOT_FOUND / UNAUTHORIZED / INVALID_USER_ID actually happened.
      if (error) throw new Error(await invokeErrorMessage(error));
      return data as AdminUserDetails;
    },
  });
}
